import ICategory from "@models/ICategory";
import { FilterValues, IFilterField, IFilterSection, IFilterSelectOption } from "@models/IFilterConfig";

export interface ICatalogItemsFilterLookups {
    /** Resolves a stored category guid (preset / deep link) to its "Parent > Child" path for the chip */
    resolveCategory?: (guid: string) => Promise<IFilterSelectOption | undefined>;
}

export interface ICatalogItemsFilterContext {
    /** Root categories of the catalog (the same list the legacy drill-down picker starts from) */
    categories: ICategory[];
    /** Supplier flags that gate fields exactly like the legacy CatalogItemFilter */
    hasNewReleases: boolean;
    hasReadyToShip: boolean;
    hasCommonItems: boolean;
    hasQbo: boolean;
    hasQbd: boolean;
    /** Hidden features are on, so the QBO item id lookup shows (legacy parity) */
    showHiddenFeatures: boolean;
    lookups: ICatalogItemsFilterLookups;
    /** Translate a message id (used for static option labels) */
    formatMessage: (id: string) => string;
}

/**
 * `getCatalogItems` takes the category guid as its own socket field; every other filter rides in the
 * queryString. The wrapper splits this key off before building the queryString.
 */
export const CATALOG_ITEMS_CATEGORY_GUID_KEY = "categoryGuid";

/** Baseline Catalog Overview filters: the legacy filter starts (and resets) with Only Active on. */
export const CATALOG_ITEMS_DEFAULT_FILTERS: FilterValues = { active: true };

const ITEM_TYPES = ["kit", "inventory", "non_inventory", "product_group", "product_group_item"];

/**
 * Compile the Catalog Overview filter config. Field keys ARE the `getCatalogItems` params (queryString
 * params plus the `categoryGuid` socket field) so presets store what the screen sends.
 *
 * Both legacy category filters are kept: `categoryGuid` is the drill-down picker (matches the category and
 * its subcategories) and `category` is the free-text category name lookup. The API applies them together
 * when both are set.
 */
export function buildCatalogItemsFilterSections(ctx: ICatalogItemsFilterContext): IFilterSection[] {
    const { formatMessage } = ctx;

    const typeOptions: IFilterSelectOption[] = [
        { label: formatMessage("all"), value: "" },
        ...ITEM_TYPES.map((type) => ({ label: formatMessage(type), value: type })),
    ];

    // The API reads these as the strings "true" / "false" (connected / not connected); absent = either.
    const connectedOptions: IFilterSelectOption[] = [
        { label: formatMessage("any"), value: "" },
        { label: formatMessage("yes"), value: "true" },
        { label: formatMessage("no"), value: "false" },
    ];

    const search: IFilterField[] = [
        { key: "item_name", label: "item_name", type: "text", apiKey: "item_name" },
        { key: "sku", label: "sku", type: "text", apiKey: "sku" },
        { key: "description", label: "description", type: "text", apiKey: "description" },
        {
            key: CATALOG_ITEMS_CATEGORY_GUID_KEY,
            label: "select_category",
            type: "category",
            apiKey: CATALOG_ITEMS_CATEGORY_GUID_KEY,
            categories: ctx.categories,
            resolveOption: ctx.lookups.resolveCategory,
        },
        { key: "category", label: "category_name", type: "text", apiKey: "category" },
        { key: "type", label: "type", type: "select", apiKey: "type", options: typeOptions },
        { key: "active", label: "only_active", type: "toggle", apiKey: "active" },
    ];

    if (ctx.hasNewReleases) {
        search.push({ key: "is_new_release", label: "is_new_release", type: "toggle", apiKey: "is_new_release" });
    }

    if (ctx.hasReadyToShip) {
        search.push({ key: "is_ready_to_ship", label: "is_ready_to_ship", type: "toggle", apiKey: "is_ready_to_ship" });
    }

    if (ctx.hasCommonItems) {
        search.push({ key: "is_common_item", label: "only_common_items", type: "toggle", apiKey: "is_common_item" });
    }

    const integrations: IFilterField[] = [];

    if (ctx.hasQbo) {
        integrations.push({
            key: "qbo_connected",
            label: "qbo_connected",
            type: "select",
            apiKey: "qbo_connected",
            options: connectedOptions,
        });

        if (ctx.showHiddenFeatures) {
            integrations.push({ key: "qbo_item_id", label: "qbo_item_id", type: "text", apiKey: "qbo_item_id" });
        }
    }

    if (ctx.hasQbd) {
        integrations.push({
            key: "qbd_connected",
            label: "qbd_connected",
            type: "select",
            apiKey: "qbd_connected",
            options: connectedOptions,
        });
    }

    return [
        { id: "search", title: "search", filters: search },
        { id: "integrations", title: "integrations", filters: integrations },
    ];
}
